import { NarrationCoveragePanel } from "./NarrationCoveragePanel";
import { NarrationPlanPanel } from "./NarrationPlanPanel";
import { StoryCoveragePanel } from "./StoryCoveragePanel";
import type { StoryFidelity } from "./storyFidelity";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { bindEchoPlayer } from "../echo-explainer/echoPlayerBridge";
import { Badge } from "@/components/ui/badge";
import {
  IdentityReferenceSceneBadge,
  IdentityReferenceSummaryBadge,
} from "./IdentityReferenceBadge";
import {
  buildIdentityReferenceScenes,
  lookupIdentityReferenceScene,
} from "./identityReferences";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AudioLines,
  Captions,
  CaptionsOff,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Maximize2,
  ScanEye,
} from "lucide-react";

import { cn } from "@/lib/utils";

import type {
  EchoflicksSessionEvent,
  EchoflicksSessionMedia,
  EchoflicksVideoSession,
} from "../types";
import { summarizeTitleDecision, type SceneI2VAudit } from "./scriptStory";
import { lookupSceneAudit } from "./echoStateManifest";
import { I2VModelBadge } from "./I2VModelBadge";
import { buildOrientationLockIndex } from "./orientationLock";
import {
  EchoFeedbackButton,
  type EchoFeedbackContext,
} from "./EchoFeedbackButton";
import { EchoSaveButton } from "./EchoSaveButton";
import { NarrationAbPanel } from "./NarrationAbPanel";
import { summarizeNarrationAb } from "./narrationAb";
import {
  DirectorNarrationContent,
  NarrationSummaryBadges,
} from "./DirectorNarrationContent";
import { NarrationCaptions } from "./NarrationCaptions";
import { NARRATION_ABSENT_COPY } from "./narrationLabels";
import { useDirectorNarration } from "./useDirectorNarration";
import { SceneStrip } from "./SceneStrip";
import {
  SourcePanel,
  SourcePrefetch,
  timelineSourceLabel,
} from "./SourcePanel";
import { parseManifestFraming } from "./manifestFraming";
import { buildSceneRoomIndex, resolveSceneSources } from "./syncMedia";
import {
  buildSyncTimeline,
  sceneIndexAtTime,
  type SyncScene,
  type SyncTimeline,
} from "./syncTimeline";
import { resolveMediaSource } from "./mediaSource";
import {
  sessionFinalVideoSource,
  useMediaUrl,
  type MediaUrlSource,
} from "./useMediaUrl";
import {
  buildPortraitPersonRetakeIndex,
  portraitPersonRetakeForSource,
} from "./portraitPersonRetake";
import { seekVideoToFeedbackTimestampWhenReady } from "../feedback/feedbackDeepLink";
import type { IdentityReport } from "./IdentityWorkbench/identityWorkbenchContracts";

// ---------------------------------------------------------------------------
// EchoDirectorView — "Director View · synced original" (QCSV-003/004).
//
// The page's first surface, and the only player on it. Side-by-side: the
// delivered video and the ORIGINAL uploaded media for the scene under the
// playhead, full frame at native aspect ratio. A proportional scene strip seeks
// on click; ←/→ step scenes; C toggles the narration captions under the
// player; N toggles the narration panel (lanes + transcript) under the strip.
//
// One stage, two surfaces: the inline card and the fullscreen dialog render the
// same `stage()` tree with the same state and the same shortcuts — fullscreen
// only removes the app chrome around it. Each surface has its own <video> (a
// single element cannot live in two places), and opening/closing the dialog
// hands playback across at the same timestamp.
//
// Sync driver: requestVideoFrameCallback (per composited frame) PLUS a
// `timeupdate` backstop — rVFC alone stops firing when the tab is backgrounded
// or the video leaves the compositor (videojs #7918), so both drivers feed the
// same idempotent index update. Scene lookup happens outside React state; we
// re-render only when the active index actually changes, with a small
// hysteresis so boundary jitter can't flicker the panel.
// ---------------------------------------------------------------------------

/** Ignore backward index flips within this distance of a boundary (jitter). */
const BOUNDARY_HYSTERESIS_SECONDS = 0.12;
/** Land seeks slightly inside the scene so the panel switches with certainty. */
const SEEK_NUDGE_SECONDS = 0.05;

/**
 * Operator view preferences, remembered per browser and shared by both
 * surfaces. The captions key predates the inline stage (captions used to be
 * fullscreen-only) and is kept so nobody loses a remembered choice.
 */
const PREF_KEYS = {
  narrationPanel: "echoflicks.director-view.narration-panel",
  captions: "echoflicks.director-view.fullscreen-captions",
} as const;

/**
 * Inline stage geometry. The dialog pins its grid to the viewport and lets the
 * media fill the panes; inline, the stage sits on a light card under the page
 * chrome, so it is content-sized instead: the player and the original each
 * grow to their natural size up to this cap (the viewport minus the chrome
 * above the stage), and the page scrolls. That keeps a 16:9 film from
 * floating in a viewport-tall black box while still giving both panes most of
 * the screen. Below `md` the panes stack and keep the old 70vh cap.
 */
const INLINE_MEDIA_CAP_CLASS = "max-h-[70vh] md:max-h-[calc(100dvh-16rem)]";

type StageSlot = "inline" | "dialog";

/**
 * A boolean the operator toggles, remembered across sessions. Captions default
 * ON (the whole point of a synced view is reading what is said over what is
 * shown); the heavier narration panel defaults OFF.
 */
function usePersistentToggle(
  storageKey: string,
  fallback: boolean,
): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return fallback;
    try {
      const stored = window.localStorage.getItem(storageKey);
      return stored == null ? fallback : stored === "on";
    } catch {
      return fallback;
    }
  });

  const update = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        window.localStorage.setItem(storageKey, next ? "on" : "off");
      } catch {
        // A blocked localStorage only costs the remembered preference.
      }
    },
    [storageKey],
  );

  return [value, update];
}

type SyncDriverTarget = {
  timeline: SyncTimeline | null;
  onIndexChange: (index: number) => void;
};

/**
 * Attach the dual playhead driver to a video element. Both callbacks compute
 * the candidate index and only push state on a real change; backward flips
 * right at a boundary are ignored (hysteresis) unless they came from a seek.
 *
 * Takes the element as STATE (callback ref upstream), not a ref object — the
 * video mounts only after its signed URL resolves, and an effect keyed on a
 * ref would never re-run to attach the listeners.
 */
function useSyncDriver(
  video: HTMLVideoElement | null,
  target: SyncDriverTarget,
) {
  const displayedIndexRef = useRef<number>(-2);
  const seekingRef = useRef(false);
  const { timeline, onIndexChange } = target;

  useEffect(() => {
    displayedIndexRef.current = -2;
  }, [timeline]);

  useEffect(() => {
    if (!video || !timeline || timeline.source === "manual") return;

    const evaluate = (mediaTime?: number) => {
      const seconds = mediaTime ?? video.currentTime;
      if (!Number.isFinite(seconds)) return;
      const nextIndex = sceneIndexAtTime(timeline, seconds);
      const displayed = displayedIndexRef.current;
      if (nextIndex === displayed) return;

      if (!seekingRef.current && displayed >= 0) {
        // Hysteresis: while playing forward, a backward flip right at the
        // previous boundary is jitter, not a real transition.
        const displayedScene = timeline.scenes.find(
          (scene) => scene.index === displayed,
        );
        if (
          displayedScene?.startSeconds != null &&
          nextIndex >= 0 &&
          nextIndex < displayed &&
          displayedScene.startSeconds - seconds < BOUNDARY_HYSTERESIS_SECONDS &&
          seconds < displayedScene.startSeconds
        ) {
          return;
        }
      }
      seekingRef.current = false;
      displayedIndexRef.current = nextIndex;
      onIndexChange(nextIndex);
    };

    const onTimeUpdate = () => evaluate();
    const onSeeked = () => {
      seekingRef.current = true;
      evaluate();
    };
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("seeked", onSeeked);

    let frameHandle: number | null = null;
    const supportsRvfc = "requestVideoFrameCallback" in video;
    if (supportsRvfc) {
      const onFrame = (
        _now: DOMHighResTimeStamp,
        metadata: VideoFrameCallbackMetadata,
      ) => {
        evaluate(metadata.mediaTime);
        frameHandle = video.requestVideoFrameCallback(onFrame);
      };
      frameHandle = video.requestVideoFrameCallback(onFrame);
    }

    evaluate();

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("seeked", onSeeked);
      if (supportsRvfc && frameHandle != null) {
        video.cancelVideoFrameCallback(frameHandle);
      }
    };
  }, [video, timeline, onIndexChange]);
}

export function EchoDirectorView({
  record,
  envelope,
  media,
  events,
  manifestBody,
  sceneI2VByScene,
  finalVideoSource,
  isAdmin,
  loading,
  feedbackContext,
  feedbackTimestampMs,
  identityReport,
  identityMode,
  storyFidelity,
}: {
  record: EchoflicksVideoSession;
  envelope: Record<string, unknown> | undefined;
  media: EchoflicksSessionMedia[];
  events: EchoflicksSessionEvent[] | undefined;
  /** Raw parsed echo-state.v1.json body (null while loading / unavailable). */
  manifestBody: unknown;
  sceneI2VByScene?: Map<string | number, SceneI2VAudit> | null;
  finalVideoSource: MediaUrlSource | null;
  isAdmin?: boolean;
  loading?: boolean;
  /** Authorized, bounded evidence report used only for source/frame correlation. */
  identityReport?: IdentityReport | null;
  identityMode?: string | null;
  /** Context the feedback composer attaches to a report filed from this player. */
  feedbackContext?: EchoFeedbackContext;
  /**
   * From a feedback deep link (`?feedbackTimestampMs=…#echo-final-video`):
   * seek the player there once its metadata is in and scroll the stage into view.
   */
  feedbackTimestampMs?: number | null;
  /** Customer-story ledger and coverage (CSF-034), parsed from the script. */
  storyFidelity?: StoryFidelity;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const dialogVideoRef = useRef<HTMLVideoElement | null>(null);
  // Elements as state so the driver effect re-attaches when the players mount
  // (they render only after the signed URL resolves).
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [dialogVideoEl, setDialogVideoEl] = useState<HTMLVideoElement | null>(
    null,
  );
  const finalVideo = useMediaUrl(finalVideoSource ?? undefined);
  // Keep the player mounted through signed-URL refreshes: a refresh momentarily
  // nulls the url, and unmount/remount would detach the sync-driver listeners.
  const lastVideoUrlRef = useRef<string | null>(null);
  if (finalVideo.url) lastVideoUrlRef.current = finalVideo.url;
  const playerUrl = finalVideo.url ?? lastVideoUrlRef.current;
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [fullscreen, setFullscreen] = useState(false);
  const [captionsOn, setCaptionsOn] = usePersistentToggle(
    PREF_KEYS.captions,
    true,
  );
  const [panelOpen, setPanelOpen] = usePersistentToggle(
    PREF_KEYS.narrationPanel,
    false,
  );

  const finalVideoStoragePath = resolveMediaSource(
    finalVideoSource,
    sessionFinalVideoSource(record),
  ).storage_path;

  const timeline = useMemo(
    () =>
      buildSyncTimeline({
        envelope,
        manifest: manifestBody,
        events,
        videoDurationSeconds: videoDuration,
        sessionWorkloadId: record.workload_id ?? null,
        finalVideoStoragePath,
      }),
    [
      envelope,
      manifestBody,
      events,
      videoDuration,
      record.workload_id,
      finalVideoStoragePath,
    ],
  );

  const sources = useMemo(
    () => resolveSceneSources({ envelope, media, sceneI2VByScene }),
    [envelope, media, sceneI2VByScene],
  );
  // Room map (FPR-033): the room each scene's photo was assigned to, for the
  // strip tooltip — the per-scene badge lives in SourcePanel.
  const sceneRooms = useMemo(() => buildSceneRoomIndex(sources), [sources]);
  // Framing overlay data (QCSV-007) — present only on framing-active echoes.
  const framingByScene = useMemo(
    () => parseManifestFraming(manifestBody),
    [manifestBody],
  );
  // Title-card debugging: the same decision summary EchoTitleSection shows.
  const titleDecision = useMemo(
    () => summarizeTitleDecision(envelope),
    [envelope],
  );
  // Operator-only second opinion on the narration (diagnostics, never delivered).
  const narrationAb = useMemo(() => summarizeNarrationAb(envelope), [envelope]);
  const identityReferenceScenes = useMemo(
    () => buildIdentityReferenceScenes(envelope, media),
    [envelope, media],
  );
  const [overlayEnabled, setOverlayEnabled] = useState(false);
  const portraitRetakes = useMemo(
    () => buildPortraitPersonRetakeIndex(events),
    [events],
  );
  // Orientation lock ("frozen position"): usually only a couple of an echo's
  // scenes anchor a captured facing direction, so mark them on the strip —
  // otherwise the per-scene badge is only findable by scrubbing onto it.
  const orientationLocked = useMemo(
    () =>
      buildOrientationLockIndex(timeline?.scenes ?? [], (scene) =>
        lookupSceneAudit(sceneI2VByScene, scene),
      ),
    [timeline, sceneI2VByScene],
  );

  const activeVideoRef = fullscreen ? dialogVideoRef : videoRef;
  const activeVideoEl = fullscreen ? dialogVideoEl : videoEl;
  useSyncDriver(activeVideoEl, {
    timeline,
    onIndexChange: setActiveIndex,
  });

  // One narration resolution for every surface. Captions need the timeline but
  // not the waveform, so the expensive audio decode is gated on the panel.
  const narrationState = useDirectorNarration({
    envelope,
    manifestBody,
    sync: timeline,
    video: activeVideoEl,
    finalVideoSource,
    mediaBucket: finalVideoSource?.storage_bucket ?? null,
    workloadId: record.workload_id ?? null,
    finalVideoStoragePath,
    enabled: captionsOn || panelOpen,
    withPeaks: panelOpen,
  });

  // Feedback deep links land on this player now that it is the only one on
  // the page: seek once the metadata is in, then bring the stage into view.
  useEffect(() => {
    if (!videoEl || feedbackTimestampMs == null) return;
    return seekVideoToFeedbackTimestampWhenReady(
      videoEl,
      feedbackTimestampMs,
      () =>
        document
          .getElementById("echo-director-view")
          ?.scrollIntoView?.({ block: "start" }),
    );
  }, [videoEl, feedbackTimestampMs, playerUrl]);

  const seekToScene = useCallback(
    (scene: SyncScene) => {
      const video = activeVideoRef.current;
      if (scene.startSeconds != null && video) {
        video.currentTime = scene.startSeconds + SEEK_NUDGE_SECONDS;
      }
      // Manual tier (no timing): select without seeking.
      setActiveIndex(scene.index);
    },
    [activeVideoRef],
  );

  /** Seek to an arbitrary delivered-time position (narration lanes, transcript). */
  const seekToSeconds = useCallback(
    (seconds: number) => {
      const video = activeVideoRef.current;
      if (video && Number.isFinite(seconds)) {
        video.currentTime = Math.max(0, seconds + SEEK_NUDGE_SECONDS);
      }
    },
    [activeVideoRef],
  );

  const selectedForFelix = timeline?.scenes.find(
    (scene) => scene.index === activeIndex,
  );
  useEffect(() => {
    if (!selectedForFelix) return;
    return bindEchoPlayer(
      {
        echoId: String(record.id),
        sceneId: selectedForFelix.sceneId,
        index: selectedForFelix.index,
        seconds: selectedForFelix.startSeconds,
      },
      seekToSeconds,
      () => activeVideoRef.current?.pause(),
    );
  }, [record.id, selectedForFelix, seekToSeconds, activeVideoRef]);

  const stepScene = useCallback(
    (direction: 1 | -1) => {
      if (!timeline) return;
      const eligible = timeline.scenes.filter((scene) => !scene.skipped);
      if (eligible.length === 0) return;
      const position = eligible.findIndex(
        (scene) => scene.index === activeIndex,
      );
      const next =
        position === -1
          ? direction === 1
            ? eligible[0]
            : eligible[eligible.length - 1]
          : eligible[
              Math.min(Math.max(position + direction, 0), eligible.length - 1)
            ];
      seekToScene(next);
    },
    [timeline, activeIndex, seekToScene],
  );

  // The stage's shortcuts, identical on both surfaces: ←/→ scenes, C captions,
  // N narration panel. Inline they fire from anywhere inside the stage (the
  // player, the strip, the buttons); in the dialog from the focus-trapped
  // content. Text fields and chorded keys (Ctrl+C is a copy, not a toggle) are
  // left alone so the feedback composer never loses a keystroke.
  const onStageKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName ?? "")
      ) {
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        stepScene(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        stepScene(-1);
      } else if (event.key === "c" || event.key === "C") {
        event.preventDefault();
        setCaptionsOn(!captionsOn);
      } else if (event.key === "n" || event.key === "N") {
        event.preventDefault();
        setPanelOpen(!panelOpen);
      }
    },
    [stepScene, captionsOn, setCaptionsOn, panelOpen, setPanelOpen],
  );

  // Opening/closing the dialog moves playback between the two video elements.
  const onFullscreenChange = useCallback((open: boolean) => {
    const from = open ? videoRef.current : dialogVideoRef.current;
    const to = open ? dialogVideoRef : videoRef;
    const resumeAt = from?.currentTime ?? null;
    from?.pause();
    setFullscreen(open);
    if (resumeAt != null) {
      requestAnimationFrame(() => {
        const target = to.current;
        if (target) target.currentTime = resumeAt;
      });
    }
  }, []);

  const uploadedCount = media.filter(
    (item) => item.kind === "uploaded_media",
  ).length;
  // Hide only when there is nothing to show: no resolvable script AND no
  // uploaded media (plan §4.1 tier 5).
  if (!timeline && uploadedCount === 0) return null;
  if (!timeline && loading) return null;
  if (!timeline) return null;

  const activeScene =
    timeline.scenes.find((scene) => scene.index === activeIndex) ?? null;
  const activeSource = activeScene
    ? (sources.get(activeScene.index) ?? null)
    : null;
  const activeFraming = activeScene
    ? (framingByScene.get(activeScene.index) ?? null)
    : null;
  const activePortraitRetake = portraitPersonRetakeForSource(
    portraitRetakes,
    activeSource &&
      (activeSource.kind === "image" || activeSource.kind === "video")
      ? activeSource
      : null,
  );
  const activeI2V = activeScene
    ? lookupSceneAudit(sceneI2VByScene, activeScene)
    : null;
  const activeIdentityReferences = lookupIdentityReferenceScene(
    identityReferenceScenes,
    activeScene,
  );
  const neighbours = [activeIndex - 1, activeIndex + 1]
    .map((index) => sources.get(index) ?? null)
    .filter((source) => source != null);

  const aspect =
    typeof (envelope?.script as Record<string, unknown> | undefined)
      ?.aspect_ratio === "string"
      ? ((envelope!.script as Record<string, unknown>).aspect_ratio as string)
      : null;
  const isVertical = aspect === "9:16";
  const gridClass = isVertical
    ? "grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
    : "grid gap-4 md:grid-cols-2";

  const player = (slot: StageSlot) =>
    playerUrl ? (
      <video
        ref={(element) => {
          if (slot === "inline") {
            videoRef.current = element;
            setVideoEl(element);
          } else {
            dialogVideoRef.current = element;
            setDialogVideoEl(element);
          }
        }}
        src={playerUrl}
        controls
        onError={finalVideo.refresh}
        onLoadedMetadata={(event) => {
          const duration = event.currentTarget.duration;
          if (Number.isFinite(duration)) setVideoDuration(duration);
        }}
        // Dialog: fills the pane on md+ (it has a definite height there).
        // Inline: natural size up to the shared cap. Below md both fall back
        // to the viewport cap.
        className={cn(
          "w-full bg-black object-contain",
          slot === "dialog"
            ? "max-h-[70vh] md:max-h-full"
            : INLINE_MEDIA_CAP_CLASS,
        )}
        data-testid={
          slot === "inline"
            ? "director-view-video"
            : "director-view-video-dialog"
        }
      />
    ) : (
      <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-black text-sm text-white/70">
        {finalVideo.isLoading ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          "No final video available."
        )}
      </div>
    );

  /**
   * The stage: player (+ i2v badge, + captions), original, strip row with the
   * toggles, and the narration panel. Rendered once at a time — inline, or in
   * the dialog while fullscreen — so every test id and label stays unique.
   */
  const stage = (slot: StageSlot) => {
    const dark = slot === "dialog";
    const slotVideoRef = dark ? dialogVideoRef : videoRef;
    const slotVideoEl = dark ? dialogVideoEl : videoEl;
    const iconButton = cn("size-7 flex-none", dark && "text-white");
    const pressed = dark ? "bg-white/20" : "bg-accent";
    return (
      <div
        className={cn(
          "flex flex-col gap-3 outline-none",
          dark && "h-full min-h-0",
        )}
        role="group"
        aria-label="Director View stage (←/→ scenes, C captions, N narration panel)"
        // Inline: clicking anywhere on the stage puts focus here so the
        // shortcuts work at once. In the dialog the focus-trapped content owns
        // the handler; attaching it here as well would fire every key twice.
        tabIndex={dark ? undefined : -1}
        onKeyDown={dark ? undefined : onStageKeyDown}
        data-testid={
          dark ? "director-view-stage-dialog" : "director-view-stage"
        }
      >
        <div
          className={cn(
            gridClass,
            "min-h-0",
            dark ? "flex-1" : "md:items-start",
          )}
        >
          <div className="flex min-h-0 flex-col overflow-hidden rounded-lg bg-black">
            <div
              className={cn(
                "relative flex min-h-0 items-center justify-center overflow-hidden",
                dark && "flex-1",
              )}
            >
              {player(slot)}
              {/* Which i2v model rendered the scene under the playhead; hover
                  reveals the full effective prompt. Renders nothing for stills
                  and title cards. */}
              <div className="absolute left-3 top-3 z-10 max-w-[70%]">
                <I2VModelBadge i2v={activeI2V} />
              </div>
            </div>
            {captionsOn && narrationState.narration ? (
              <NarrationCaptions
                line={narrationState.activeLine}
                video={slotVideoEl}
                className="pb-2"
              />
            ) : null}
          </div>
          <div className="min-h-0 overflow-hidden">
            <SourcePanel
              scene={activeScene}
              source={activeSource}
              isAdmin={isAdmin}
              showDrift={timeline.source === "manifest_realized"}
              framing={activeFraming}
              overlayEnabled={isAdmin && overlayEnabled}
              fullBleed={dark}
              mediaMaxHeightClassName={
                dark ? undefined : INLINE_MEDIA_CAP_CLASS
              }
              titleDecision={titleDecision}
              portraitRetake={activePortraitRetake}
              identityReport={identityReport}
              identityMode={identityMode}
              i2v={activeI2V}
              identityReferenceBadge={
                activeIdentityReferences ? (
                  <IdentityReferenceSceneBadge
                    scene={activeIdentityReferences}
                  />
                ) : null
              }
            />
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          tabIndex={0}
          aria-label="Scene navigation (←/→ to step scenes)"
        >
          <Button
            variant="ghost"
            size="icon"
            className={iconButton}
            onClick={() => stepScene(-1)}
            aria-label="Previous scene"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <SceneStrip
              timeline={timeline}
              activeIndex={activeIndex}
              onSeekToScene={seekToScene}
              orientationLocked={orientationLocked}
              sceneRooms={sceneRooms}
            />
          </div>
          {narrationState.narration ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className={cn(iconButton, captionsOn && pressed)}
                onClick={() => setCaptionsOn(!captionsOn)}
                aria-pressed={captionsOn}
                aria-label={
                  captionsOn ? "Hide captions (C)" : "Show captions (C)"
                }
                title={captionsOn ? "Hide captions (C)" : "Show captions (C)"}
                data-testid={
                  dark
                    ? "director-view-fullscreen-captions-toggle"
                    : "director-view-captions-toggle"
                }
              >
                {captionsOn ? (
                  <Captions className="size-4" />
                ) : (
                  <CaptionsOff className="size-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn(iconButton, panelOpen && pressed)}
                onClick={() => setPanelOpen(!panelOpen)}
                aria-pressed={panelOpen}
                aria-label={
                  panelOpen
                    ? "Hide narration panel (N)"
                    : "Show narration panel (N)"
                }
                title={
                  panelOpen
                    ? "Hide narration panel (N)"
                    : "Show narration panel (N)"
                }
                data-testid={
                  dark
                    ? "director-view-fullscreen-panel-toggle"
                    : "director-view-panel-toggle"
                }
              >
                <AudioLines className="size-4" />
              </Button>
            </>
          ) : null}
          {/* Inline, the feedback entry point sits in the card header. */}
          {dark && playerUrl ? (
            <EchoFeedbackButton
              record={record}
              videoRef={slotVideoRef}
              videoUrl={playerUrl}
              context={{
                ...feedbackContext,
                manifest: manifestBody,
                sync: timeline,
                finalVideoStoragePath,
              }}
            />
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className={iconButton}
            onClick={() => stepScene(1)}
            aria-label="Next scene"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>

        {panelOpen ? (
          <div
            className={cn(
              "border-t pt-3",
              dark
                ? "max-h-[38vh] shrink-0 overflow-y-auto border-white/15"
                : "border-border",
            )}
            data-testid={
              dark
                ? "director-view-fullscreen-narration"
                : "director-view-narration-panel"
            }
          >
            {narrationState.narration ? (
              <>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <NarrationSummaryBadges
                    state={narrationState}
                    tone={dark ? "dark" : "surface"}
                  />
                </div>
                <DirectorNarrationContent
                  state={narrationState}
                  sync={timeline}
                  video={slotVideoEl}
                  durationSeconds={videoDuration}
                  onSeekSeconds={seekToSeconds}
                  onSelectScene={setActiveIndex}
                  isAdmin={isAdmin}
                  tone={dark ? "dark" : "surface"}
                />
              </>
            ) : narrationState.absentReason ? (
              <p
                className={cn(
                  "text-xs",
                  dark ? "text-white/70" : "text-muted-foreground",
                )}
              >
                {NARRATION_ABSENT_COPY[narrationState.absentReason]}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const badges = (
    <>
      <Badge variant="outline" title="How scene boundaries were resolved">
        {timelineSourceLabel(timeline)}
      </Badge>
      <IdentityReferenceSummaryBadge scenes={identityReferenceScenes} />
      {orientationLocked.size > 0 ? (
        <Badge
          data-testid="frozen-position-summary-badge"
          variant="outline"
          className="border-sky-500 text-sky-600"
          title={`Orientation lock fired on ${orientationLocked.size === 1 ? "1 scene" : `${orientationLocked.size} scenes`}: the rendered direction holds each subject's captured facing so a facing-away subject never turns to camera. Marked on the strip below.`}
        >
          frozen position · {orientationLocked.size}
        </Badge>
      ) : null}
      {narrationState.narration ? (
        <NarrationSummaryBadges state={narrationState} />
      ) : null}
      {isAdmin && framingByScene.size > 0 ? (
        <label className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-muted-foreground">
          Crop overlay
          <Switch
            checked={overlayEnabled}
            onCheckedChange={setOverlayEnabled}
            aria-label="Draw the framing crop window on the original"
          />
        </label>
      ) : null}
      {timeline.warnings.length > 0 && isAdmin ? (
        <Badge
          variant="outline"
          className="border-amber-500 text-amber-600"
          title={timeline.warnings.join("\n")}
        >
          {timeline.warnings.length} warning
          {timeline.warnings.length === 1 ? "" : "s"}
        </Badge>
      ) : null}
    </>
  );

  return (
    <Card id="echo-director-view">
      {/* Deep links from before the rename, and feedback links to the final
          video (which plays here), still land on this card. */}
      <span id="echo-crop-qc" aria-hidden="true" />
      <span id="echo-final-video" aria-hidden="true" />
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <ScanEye className="size-4" />
          Director View — synced original
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {badges}
          <EchoSaveButton echoId={String(record.id)} labelled />
          {playerUrl ? (
            <EchoFeedbackButton
              record={record}
              videoRef={videoRef}
              videoUrl={playerUrl}
              context={{
                ...feedbackContext,
                manifest: manifestBody,
                sync: timeline,
                finalVideoStoragePath,
              }}
            />
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onFullscreenChange(true)}
          >
            <Maximize2 className="mr-1.5 size-3.5" />
            Fullscreen
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {fullscreen ? (
          <div className="flex h-24 items-center justify-center rounded-lg bg-muted/30 text-sm text-muted-foreground">
            Playing in fullscreen
          </div>
        ) : (
          stage("inline")
        )}
        {neighbours.map((source, position) => (
          <SourcePrefetch key={position} source={source} />
        ))}
        <NarrationCoveragePanel
          manifest={manifestBody}
          onSeek={seekToSeconds}
          workloadId={record.workload_id}
          finalVideoKey={finalVideoStoragePath}
        />
        <NarrationPlanPanel
          manifest={manifestBody}
          onSeek={seekToSeconds}
          workloadId={record.workload_id}
          finalVideoKey={finalVideoStoragePath}
        />
        {isAdmin && storyFidelity ? (
          <StoryCoveragePanel fidelity={storyFidelity} />
        ) : null}

        {isAdmin && narrationAb ? (
          <div className="mt-4 border-t pt-3">
            <p className="mb-2 text-xs font-medium">
              Narration A/B — what shipped vs a second model on the same brief
            </p>
            <NarrationAbPanel comparison={narrationAb} />
          </div>
        ) : null}
      </CardContent>

      <Dialog open={fullscreen} onOpenChange={onFullscreenChange}>
        <DialogContent
          className="h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-none gap-0 overflow-hidden border-white/15 bg-black p-4 text-white sm:h-[calc(100dvh-2rem)] sm:max-w-[calc(100vw-2rem)] [&>[data-slot=dialog-close]]:z-20 [&>[data-slot=dialog-close]]:text-white"
          onKeyDown={onStageKeyDown}
        >
          <DialogTitle className="sr-only">
            Director View fullscreen — synced original
          </DialogTitle>
          {fullscreen ? stage("dialog") : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
